// The vendor import's preview (owner, 2026-10-20): every vendor the Apply
// touches, in `ImportChangeList`'s four groups — added, activated, edited,
// deactivated, the owner's headings, each singular or plural with its count. A
// row is the vendor's name over its code; a vendor the file leaves out says so
// under its status.

import { VENDOR_IMPORT_FIELD_LABEL, type VendorImportPreview } from '../api/types'
import { countedHeading } from '../lib/format'
import { ImportChangeList, type ChangeLine, type ChangeRow } from './ImportChangeList'

const status = (before: 'Active' | 'Inactive', after: 'Active' | 'Inactive'): ChangeLine => ({
  label: 'Status',
  before,
  after,
})

export function VendorImportChanges({ preview }: { preview: VendorImportPreview }) {
  const goingInactive = new Set(preview.marked_inactive.map((v) => v.vendor_id))
  const goingActive = new Set(preview.marked_active.map((v) => v.vendor_id))
  const edited = new Set(preview.updates.map((u) => u.vendor_id))
  const row = (code: string, name: string | null | undefined, lines: ChangeLine[]): ChangeRow => ({
    key: code,
    title: name || code,
    subtitle: code,
    lines,
  })

  const added = preview.adds.map((a) =>
    row(a.values.vendor_id, a.values.vendor_name, [{ note: a.inactive ? 'New vendor, added as Inactive' : 'New vendor' }]),
  )
  const activated = preview.marked_active
    .filter((v) => !edited.has(v.vendor_id))
    .map((v) => row(v.vendor_id, v.vendor_name, [status('Inactive', 'Active')]))
  const changed = preview.updates.map((u) =>
    row(u.vendor_id, u.vendor_name, [
      ...(goingInactive.has(u.vendor_id) ? [status('Active', 'Inactive')] : []),
      ...(goingActive.has(u.vendor_id) ? [status('Inactive', 'Active')] : []),
      ...u.changes.map((c) => ({ label: VENDOR_IMPORT_FIELD_LABEL[c.field], before: c.before, after: c.after })),
    ]),
  )
  const deactivated = [
    ...preview.marked_inactive
      .filter((v) => !edited.has(v.vendor_id))
      .map((v) => row(v.vendor_id, v.vendor_name, [status('Active', 'Inactive')])),
    ...preview.absent.map((v) => row(v.vendor_id, v.vendor_name, [status('Active', 'Inactive'), { note: 'Not in the file' }])),
  ]
  // `absent` is a sample on the wire; `absent_total` is the true count.
  const deactivatedTotal = deactivated.length + preview.absent_total - preview.absent.length

  return (
    <ImportChangeList
      empty="Nothing in this file changes the vendor list."
      groups={[
        { heading: countedHeading(added.length, 'Vendor', 'Added'), rows: added },
        { heading: countedHeading(activated.length, 'Vendor', 'Activated'), rows: activated },
        { heading: countedHeading(changed.length, 'Vendor', 'Edited'), rows: changed },
        { heading: countedHeading(deactivatedTotal, 'Vendor', 'Deactivated'), rows: deactivated, total: deactivatedTotal },
      ]}
    />
  )
}
